import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Link2, FileText, Video, File, Plus, Trash2, Edit2, Star, ExternalLink, Image } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const RESOURCE_TYPES = [
  { value: 'link', label: 'Link', icon: Link2 },
  { value: 'document', label: 'Document', icon: FileText },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'file', label: 'File', icon: File }
];

const CATEGORIES = [
  'General',
  'NVC Basics',
  'Practice Exercises',
  'Audio/Video',
  'Reading Materials',
  'Community',
  'External Resources'
];

export default function ResourcesManager({ token }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    resource_type: 'link',
    url: '',
    thumbnail_url: '',
    category: 'General',
    is_featured: false
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/resources`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setResources(data.resources || []);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.url) {
      toast.error('Please fill in title and URL');
      return;
    }

    try {
      const url = editingResource 
        ? `${BACKEND_URL}/api/admin/resources/${editingResource._id}`
        : `${BACKEND_URL}/api/admin/resources`;
      
      const response = await fetch(url, {
        method: editingResource ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingResource ? 'Resource updated!' : 'Resource created!');
        fetchResources();
        resetForm();
      } else {
        toast.error('Failed to save resource');
      }
    } catch (error) {
      toast.error('Error saving resource');
    }
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/resources/${resourceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Resource deleted');
        fetchResources();
      }
    } catch (error) {
      toast.error('Error deleting resource');
    }
  };

  const handleEdit = (resource) => {
    setEditingResource(resource);
    setFormData({
      title: resource.title,
      description: resource.description || '',
      resource_type: resource.resource_type,
      url: resource.url,
      thumbnail_url: resource.thumbnail_url || '',
      category: resource.category || 'General',
      is_featured: resource.is_featured || false
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingResource(null);
    setFormData({
      title: '',
      description: '',
      resource_type: 'link',
      url: '',
      thumbnail_url: '',
      category: 'General',
      is_featured: false
    });
  };

  const getTypeIcon = (type) => {
    const typeConfig = RESOURCE_TYPES.find(t => t.value === type);
    const Icon = typeConfig?.icon || Link2;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-400">Loading resources...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Student Resources</h2>
          <p className="text-slate-600 text-sm">Manage links, documents, and files for students</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-teal-600 hover:bg-teal-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Resource
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="border-teal-200 bg-teal-50/50">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">
              {editingResource ? 'Edit Resource' : 'Add New Resource'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Resource title"
                    className="bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <Select
                    value={formData.resource_type}
                    onValueChange={(value) => setFormData({...formData, resource_type: value})}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">URL *</label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  placeholder="https://example.com/resource"
                  className="bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <span className="flex items-center gap-2">
                    <Image className="h-4 w-4 text-teal-600" />
                    Thumbnail Image URL (optional)
                  </span>
                </label>
                <Input
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({...formData, thumbnail_url: e.target.value})}
                  placeholder="https://example.com/thumbnail.jpg"
                  className="bg-white"
                />
                {formData.thumbnail_url && (
                  <div className="mt-2 p-2 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-500 mb-2">Preview:</p>
                    <img 
                      src={formData.thumbnail_url} 
                      alt="Thumbnail preview" 
                      className="h-20 w-32 object-cover rounded border"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of this resource"
                  className="bg-white"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({...formData, category: value})}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-700">Featured resource</span>
                    <Star className={`h-4 w-4 ${formData.is_featured ? 'text-amber-500 fill-amber-500' : 'text-slate-400'}`} />
                  </label>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                  {editingResource ? 'Update Resource' : 'Create Resource'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Resources List */}
      <div className="space-y-3">
        {resources.length === 0 ? (
          <Card className="bg-slate-50">
            <CardContent className="py-8 text-center text-slate-500">
              No resources yet. Add your first resource above.
            </CardContent>
          </Card>
        ) : (
          resources.map(resource => (
            <Card key={resource._id} className={`hover:shadow-md transition-shadow ${resource.is_featured ? 'border-amber-300 bg-amber-50/30' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    {/* Thumbnail or Icon */}
                    {resource.thumbnail_url ? (
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200">
                        <img 
                          src={resource.thumbnail_url} 
                          alt={resource.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full bg-teal-100 flex items-center justify-center"><svg class="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path></svg></div>';
                          }}
                        />
                      </div>
                    ) : (
                      <div className={`p-2 rounded-lg ${
                        resource.resource_type === 'video' ? 'bg-purple-100 text-purple-600' :
                        resource.resource_type === 'document' ? 'bg-blue-100 text-blue-600' :
                        resource.resource_type === 'file' ? 'bg-green-100 text-green-600' :
                        'bg-teal-100 text-teal-600'
                      }`}>
                        {getTypeIcon(resource.resource_type)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-800 truncate">{resource.title}</h3>
                        {resource.is_featured && (
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500 flex-shrink-0" />
                        )}
                        {resource.thumbnail_url && (
                          <Image className="h-3 w-3 text-teal-500 flex-shrink-0" title="Has thumbnail" />
                        )}
                      </div>
                      {resource.description && (
                        <p className="text-sm text-slate-600 mt-1 line-clamp-1">{resource.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span className="px-2 py-0.5 bg-slate-100 rounded">{resource.category}</span>
                        <a 
                          href={resource.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-teal-600 hover:text-teal-700"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(resource)}
                      className="text-slate-600 hover:text-teal-600"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(resource._id)}
                      className="text-slate-600 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
